import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/utils';
import { BedDouble, Bath, Ruler, MapPin, ExternalLink, Loader2, Home } from 'lucide-react';
import { ExternalListingsSection } from '@/components/ExternalListingsSection';

const REASONS = {
  propietario: 'Este lead es Propietario, así que no se buscan inmuebles.',
  sin_tipo: 'Elige el tipo de cliente (Comprador o Arrendador) en la pestaña Datos para buscar inmuebles.',
};

const Card = ({ p, features }) => (
  <a href={p.link} target="_blank" rel="noopener noreferrer" data-testid={`listing-card-${p.id}`}
    className="group flex flex-col bg-[#16302B] rounded-xl border border-white/5 overflow-hidden hover:border-[#A38560]/50 transition-colors">
    <div className="relative aspect-[4/3] bg-[#0A1F1A] overflow-hidden">
      {p.image ? <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        : <div className="w-full h-full flex items-center justify-center text-white/20"><Home size={32} /></div>}
      <div className="absolute top-2 left-2 flex gap-1.5">
        {p.is_new && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#6BCB77] text-[#03110D] font-semibold">Nuevo</span>}
        {p.source_name && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#03110D]/85 text-white font-semibold">{p.source_name}</span>}
        {p.featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#A38560] text-[#03110D] font-semibold">Destacado</span>}
        {p.over_budget && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/90 text-[#03110D] font-semibold">Sobre presupuesto</span>}
      </div>
    </div>
    <div className="p-3 space-y-2 flex-1 flex flex-col">
      <p className="text-sm text-white font-medium line-clamp-2">{p.title}</p>
      <p className="text-xs text-white/50 flex items-center gap-1"><MapPin size={12} />{p.zone || '—'}</p>
      <p className="font-outfit text-base font-semibold text-[#A38560] tabular-nums">{formatCOP(p.price)}</p>
      <div className="flex items-center gap-3 text-xs text-white/60 tabular-nums">
        <span className="flex items-center gap-1"><BedDouble size={13} />{p.bedrooms}</span>
        <span className="flex items-center gap-1"><Bath size={13} />{p.bathrooms}</span>
        <span className="flex items-center gap-1"><Ruler size={13} />{p.area ? `${p.area} m²` : '—'}</span>
      </div>
      {features.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {features.map(f => {
            const ok = p.matched_features.includes(f);
            return <span key={f} className={`text-[10px] px-1.5 py-0.5 rounded-md ${ok ? 'bg-[#6BCB77]/15 text-[#6BCB77]' : 'bg-white/5 text-white/30 line-through'}`}>{f}</span>;
          })}
        </div>
      )}
      <span className="mt-auto pt-1 text-xs text-[#A38560] flex items-center gap-1 group-hover:text-[#C4A87C]">{p.source_name ? `Ver en ${p.source_name}` : 'Ver en WASI'} <ExternalLink size={12} /></span>
    </div>
  </a>
);

const Criteria = ({ c, operation }) => (
  <div data-testid="listings-criteria" className="flex flex-wrap gap-2 text-[11px]">
    <span className="px-2 py-1 rounded-lg bg-[#A38560]/15 text-[#C4A87C] capitalize">{operation}</span>
    <span className="px-2 py-1 rounded-lg bg-white/5 text-white/60">Zona: {c.sectors.length ? c.sectors.join(', ') : 'todas'}</span>
    <span className="px-2 py-1 rounded-lg bg-white/5 text-white/60">Presupuesto: {c.budget ? `hasta ${formatCOP(c.budget * 1.15)}` : 'sin límite'}</span>
    <span className="px-2 py-1 rounded-lg bg-white/5 text-white/60">Habitaciones: {c.rooms ? `${c.rooms}+` : 'cualquiera'}</span>
    <span className="px-2 py-1 rounded-lg bg-white/5 text-white/60">Área: {c.min_area ? `desde ${Math.round(c.min_area * 0.8)} m²` : 'cualquiera'}</span>
    {c.features.length > 0 && <span className="px-2 py-1 rounded-lg bg-white/5 text-white/60">Características: {c.features.join(', ')}</span>}
  </div>
);

const GROUPS = [
  { key: 'all', label: 'Cumplen con todo', dot: 'bg-[#6BCB77]' },
  { key: 'partial', label: 'Cumplen algunas características', dot: 'bg-amber-400' },
  { key: 'none', label: 'Sin las características deseadas', dot: 'bg-white/30' },
];

const Grid = ({ items, features }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
    {items.map(p => <Card key={p.id} p={p} features={features} />)}
  </div>
);

export const ResultGroups = ({ items, features, prefix = 'listings' }) => (
  features.length === 0 ? <Grid items={items} features={[]} /> : GROUPS.map(g => {
    const list = items.filter(p => p.group === g.key);
    if (!list.length) return null;
    return (
      <div key={g.key} data-testid={`${prefix}-group-${g.key}`} className="space-y-3">
        <h4 className="text-sm font-medium text-white flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${g.dot}`} />{g.label} <span className="text-white/30 font-normal">({list.length})</span></h4>
        <Grid items={list} features={features} />
      </div>
    );
  })
);

const NewMatches = ({ items, features }) => (
  <div data-testid="listings-new-matches" className="rounded-2xl border border-[#6BCB77]/30 bg-[#6BCB77]/[0.04] p-4 space-y-3">
    <h4 className="text-sm font-medium text-white flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#6BCB77] animate-pulse" />Nuevas coincidencias <span className="text-white/30 font-normal">({items.length})</span></h4>
    <Grid items={items} features={features} />
  </div>
);

const Results = ({ data }) => {
  const features = data.criteria.features;
  const fresh = [...data.items, ...(data.external?.items || [])].filter(p => p.is_new);
  const wasi = data.items.filter(p => !p.is_new);
  const external = data.external && { ...data.external, items: data.external.items.filter(p => !p.is_new) };
  return (
    <>
      <Criteria c={data.criteria} operation={data.operation} />
      {fresh.length > 0 && <NewMatches items={fresh} features={features} />}
      <p data-testid="listings-count" className="text-xs text-white/50">{wasi.length} inmueble(s) encontrados</p>
      {wasi.length === 0 ? <p className="text-sm text-white/40 py-6 text-center">No hay {fresh.length ? 'más ' : ''}inmuebles en WASI que coincidan con esta búsqueda.</p> : (
        <ResultGroups items={wasi} features={features} />
      )}
      {external && <ExternalListingsSection external={external} features={features} />}
    </>
  );
};

export const LeadListings = ({ leadId, refreshKey }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setData(null); setError('');
    api.get(`/crm/leads/${leadId}/wasi-listings`).then(setData).catch(() => setError('No se pudo consultar WASI. Intenta de nuevo en un momento.'));
  }, [leadId, refreshKey]);

  return (
    <div data-testid="lead-listings" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
      <div>
        <h3 className="font-outfit text-lg font-medium text-white">Listings en WASI</h3>
        <p className="text-xs text-white/40 mt-1">Inmuebles activos o destacados que se ajustan a la búsqueda del cliente. Las características deseadas se buscan en el título, la descripción y la ficha de WASI.</p>
      </div>
      {error && <p data-testid="listings-error" className="text-sm text-red-400">{error}</p>}
      {!data && !error && <div className="flex items-center gap-2 text-sm text-white/50"><Loader2 size={16} className="animate-spin" />Buscando en WASI...</div>}
      {data && !data.applies && <p data-testid="listings-not-applicable" className="text-sm text-white/50">{REASONS[data.reason]}</p>}
      {data?.applies && <Results data={data} />}
    </div>
  );
};
